import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface DocHubTip {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  content: string;
  category_id?: string;
  attachments: string[];
  tags: string[];
  slug?: string;
  created_at: string;
  updated_at: string;
}

export const useDocHubTips = (brandId: string) => {
  const queryClient = useQueryClient();

  const {
    data: tips,
    isLoading,
    error
  } = useQuery({
    queryKey: ['doc-hub-tips', brandId],
    queryFn: async (): Promise<DocHubTip[]> => {
      const { data, error } = await supabase
        .from('os_doc_hub_tips')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(tip => ({
        ...tip,
        attachments: Array.isArray(tip.attachments) ? tip.attachments : 
                    typeof tip.attachments === 'string' ? JSON.parse(tip.attachments) : [],
        tags: Array.isArray(tip.tags) ? tip.tags : 
              typeof tip.tags === 'string' ? JSON.parse(tip.tags) : [],
      }));
    },
    enabled: !!brandId,
  });

  const createTipMutation = useMutation({
    mutationFn: async (tipData: Omit<DocHubTip, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('os_doc_hub_tips')
        .insert({
          ...tipData,
          attachments: tipData.attachments,
          tags: tipData.tags,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tips', brandId] });
      toast({
        title: "Success",
        description: "Tip created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating tip:', error);
      toast({
        title: "Error",
        description: "Failed to create tip",
        variant: "destructive",
      });
    },
  });

  const updateTipMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DocHubTip> & { id: string }) => {
      const updateData: any = { ...updates };
      if (updates.attachments !== undefined) {
        updateData.attachments = updates.attachments;
      }
      if (updates.tags !== undefined) {
        updateData.tags = updates.tags;
      }

      const { data, error } = await supabase
        .from('os_doc_hub_tips')
        .update(updateData)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tips', brandId] });
      toast({
        title: "Success",
        description: "Tip updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating tip:', error);
      toast({
        title: "Error",
        description: "Failed to update tip",
        variant: "destructive",
      });
    },
  });

  const deleteTipMutation = useMutation({
    mutationFn: async (tipId: string) => {
      const { error } = await supabase
        .from('os_doc_hub_tips')
        .delete()
        .eq('id', tipId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-hub-tips', brandId] });
      toast({
        title: "Success",
        description: "Tip deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error deleting tip:', error);
      toast({
        title: "Error",
        description: "Failed to delete tip",
        variant: "destructive",
      });
    },
  });

  const createTip = (tipData: Omit<DocHubTip, 'id' | 'created_at' | 'updated_at'>) => {
    createTipMutation.mutate(tipData);
  };

  const updateTip = (id: string, updates: Partial<DocHubTip>) => {
    updateTipMutation.mutate({ id, ...updates });
  };

  const deleteTip = (id: string) => {
    deleteTipMutation.mutate(id);
  };

  return {
    tips: tips || [],
    isLoading,
    error,
    createTip,
    updateTip,
    deleteTip,
    isCreating: createTipMutation.isPending,
    isUpdating: updateTipMutation.isPending,
    isDeleting: deleteTipMutation.isPending,
  };
};
